/**
 * AdminReviewsPage — `/admin/reviews`.
 *
 * Admin-only moderation surface for Rate My Pluck. Pulls
 * GET /api/admin/reviews?status=visible|hidden|all and exposes
 * inline hide/unhide via PATCH /api/admin/reviews/{id}/status.
 *
 * Design rules (per Step 1 spec):
 *   - Status filter only (no provider filter in v1).
 *   - Lean table (NO Sheet detail panel).
 *   - Long comments truncate to 2 lines with an inline "Show more"
 *     toggle (keeps the row compact without losing context).
 *   - Hidden rows render with a slate-on-light "Hidden" pill so the
 *     admin can scan moderation state quickly.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import StarRating from "@/components/reviews/StarRating";
import { Eye, EyeOff, MessageSquare } from "lucide-react";

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function CommentCell({ id, comment }) {
  const [expanded, setExpanded] = useState(false);
  if (!comment) {
    return (
      <span className="text-xs italic text-muted-foreground">No comment</span>
    );
  }
  const longish = comment.length > 140;
  if (!longish) return <span className="text-sm text-pp-text">{comment}</span>;
  return (
    <div>
      <span
        className={`text-sm text-pp-text ${expanded ? "" : "line-clamp-2"}`}
        data-testid={`admin-reviews-comment-${id}`}
      >
        {comment}
      </span>
      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="mt-1 text-xs font-semibold text-brand-orange hover:underline"
        data-testid={`admin-reviews-comment-toggle-${id}`}
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

export default function AdminReviewsPage() {
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // review id currently mutating

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/admin/reviews", { params: { status } })
      .then(({ data }) => {
        if (active) setItems(data);
      })
      .catch((e) => {
        if (active) {
          toast.error(
            formatApiErrorDetail(e?.response?.data?.detail) || e.message
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [status]);

  const setReviewStatus = async (id, next) => {
    setPending(id);
    try {
      const { data } = await api.patch(`/admin/reviews/${id}/status`, {
        status: next,
      });
      setItems((cur) => cur.map((r) => (r.id === id ? { ...r, ...data } : r)));
      toast.success(next === "hidden" ? "Review hidden." : "Review restored.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setPending(null);
    }
  };

  const filtered = useMemo(() => {
    if (status === "all") return items;
    return items.filter((r) => r.status === status);
  }, [items, status]);

  return (
    <div data-testid="admin-reviews-page">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
            Reviews
          </span>
          <h2 className="font-heading text-2xl font-bold text-pp-text">
            Rate My Pluck moderation
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hide individual reviews to remove them from the public-facing
            aggregate. Customers still see their own row marked “Hidden by
            admin”.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="admin-reviews-status-filter"
            className="text-xs font-bold uppercase tracking-wider text-pp-text"
          >
            Status
          </label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              id="admin-reviews-status-filter"
              className="w-[180px] rounded-full"
              data-testid="admin-reviews-status-filter"
            >
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="admin-reviews-status-all">
                All
              </SelectItem>
              <SelectItem
                value="visible"
                data-testid="admin-reviews-status-visible"
              >
                Visible
              </SelectItem>
              <SelectItem
                value="hidden"
                data-testid="admin-reviews-status-hidden"
              >
                Hidden
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <Card
        className="rounded-3xl border-border bg-pp-surface p-0 shadow-none"
        data-testid="admin-reviews-table-card"
      >
        {loading ? (
          <p
            className="p-8 text-center text-sm text-muted-foreground"
            data-testid="admin-reviews-loading"
          >
            Loading…
          </p>
        ) : filtered.length === 0 ? (
          <div
            className="p-10 text-center"
            data-testid="admin-reviews-empty"
          >
            <MessageSquare className="mx-auto h-10 w-10 text-brand-orange" />
            <p className="mt-3 text-sm font-semibold text-pp-text">
              No reviews to show.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="min-w-full text-left text-sm"
              data-testid="admin-reviews-table"
            >
              <thead className="border-b border-border bg-pp-v3-bg text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Rating</th>
                  <th className="px-4 py-3">Comment</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isHidden = r.status === "hidden";
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-border last:border-b-0 align-top"
                      data-testid={`admin-reviews-row-${r.id}`}
                    >
                      <td className="px-4 py-3 font-semibold text-pp-text">
                        {r.provider_business_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-pp-text">
                        {r.customer_name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StarRating
                          value={r.rating}
                          readOnly
                          size="sm"
                          testidPrefix={`admin-reviews-row-${r.id}-stars`}
                        />
                      </td>
                      <td className="px-4 py-3 max-w-md">
                        <CommentCell id={r.id} comment={r.comment} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                        {formatDateTime(r.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {isHidden ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-pp-text"
                            data-testid={`admin-reviews-row-${r.id}-status-pill`}
                          >
                            <EyeOff className="h-3 w-3" aria-hidden="true" />
                            Hidden
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-orange"
                            data-testid={`admin-reviews-row-${r.id}-status-pill`}
                          >
                            <Eye className="h-3 w-3" aria-hidden="true" />
                            Visible
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isHidden ? (
                          <Button
                            type="button"
                            size="sm"
                            disabled={pending === r.id}
                            onClick={() => setReviewStatus(r.id, "visible")}
                            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                            data-testid={`admin-reviews-unhide-${r.id}`}
                          >
                            <Eye className="mr-1 h-3 w-3" /> Unhide
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={pending === r.id}
                            onClick={() => setReviewStatus(r.id, "hidden")}
                            className="rounded-full"
                            data-testid={`admin-reviews-hide-${r.id}`}
                          >
                            <EyeOff className="mr-1 h-3 w-3" /> Hide
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
